import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Download,
  Edit3,
  ExternalLink,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import FirmwareDeleteAllDialog from "../components/firmware/FirmwareDeleteAllDialog";
import FirmwareDeleteDialog from "../components/firmware/FirmwareDeleteDialog";
import FirmwareFormDialog from "../components/firmware/FirmwareFormDialog";
import FirmwareImportDialog from "../components/firmware/FirmwareImportDialog";
import { Button } from "../components/ui/button";
import {
  DataValue,
  PassiveBadge,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnSelectFilter,
  ColumnTextFilter,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import {
  downloadFirmwareXlsx,
  exportFirmware,
  listFirmware,
  listFirmwareFilterOptions,
} from "../lib/firmwareApi";

const EMPTY_FILTERS = Object.freeze({
  model: "",
  firmware_version: "",
  firmware_type: "",
  android_version: "",
  firmware_url: "",
  apdb_url: "",
  status: "",
  updated_date: "",
});

const FIRMWARE_TABLE_GEOMETRY = Object.freeze([
  { key: "model", kind: "fill", minWidth: 240 },
  { key: "firmware-version", kind: "fixed", width: 270 },
  { key: "type", kind: "fixed", width: 110 },
  { key: "android-version", kind: "fixed", width: 160 },
  { key: "firmware", kind: "fixed", width: 110 },
  { key: "apdb", kind: "fixed", width: 180 },
  { key: "status", kind: "fixed", width: 100 },
  { key: "updated-at", kind: "fixed", width: 170 },
  { key: "actions", kind: "utility", width: 104 },
]);
const FIRMWARE_STAFF_TABLE_GEOMETRY = Object.freeze(FIRMWARE_TABLE_GEOMETRY.slice(0, -1));

function formatUpdatedAt(value) {
  return formatPresentationTimestamp(value);
}

function ExternalFirmwareLink({ href, label, testid }) {
  if (!href) return "—";
  return (
    <Button asChild variant="export" size="row-icon">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        aria-label={`Download ${label}`}
        title={`Download ${label}`}
      >
        <ExternalLink aria-hidden="true" />
      </a>
    </Button>
  );
}

export default function Firmware() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterDrafts, setFilterDrafts] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    models: [],
    firmware_types: [],
    android_versions: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [optionsRetryKey, setOptionsRetryKey] = useState(0);
  const [editing, setEditing] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportInFlight = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    listFirmwareFilterOptions({ signal: controller.signal })
      .then((options) => {
        setFilterOptions({
          models: Array.isArray(options?.models) ? options.models : [],
          firmware_types: Array.isArray(options?.firmware_types)
            ? options.firmware_types
            : [],
          android_versions: Array.isArray(options?.android_versions)
            ? options.android_versions
            : [],
        });
        setOptionsError("");
      })
      .catch((requestError) => {
        if (
          requestError?.code !== "ERR_CANCELED" &&
          requestError?.name !== "CanceledError"
        ) {
          setOptionsError("Filter options are unavailable. The Firmware list is still available.");
        }
      });
    return () => controller.abort();
  }, [optionsRetryKey]);

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const response = await listFirmware(
          { ...filters, page, page_size: pageSize },
          { signal }
        );
        const nextRecords = Array.isArray(response?.data) ? response.data : [];
        const nextPagination = response?.pagination || {
          page,
          page_size: pageSize,
          total: nextRecords.length,
          total_pages: nextRecords.length ? 1 : 0,
        };
        const lastPage = Math.max(nextPagination.total_pages || 0, 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setRecords(nextRecords);
        setPagination(nextPagination);
      } catch (requestError) {
        if (
          requestError?.code !== "ERR_CANCELED" &&
          requestError?.name !== "CanceledError"
        ) {
          setRecords([]);
          setPagination({
            page,
            page_size: pageSize,
            total: 0,
            total_pages: 0,
          });
          setError(formatApiError(requestError));
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [filters, page, pageSize]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, retryKey]);

  function updateFilter(key, value) {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  }

  function clearFilters() {
    setFilterDrafts(EMPTY_FILTERS);
    setPage(1);
    setFilters(EMPTY_FILTERS);
  }

  function refreshAfterSave(isCreate) {
    setEditing(undefined);
    setOptionsRetryKey((value) => value + 1);
    if (isCreate && page !== 1) {
      setPage(1);
    } else {
      setRetryKey((value) => value + 1);
    }
  }

  function refreshAfterDelete() {
    setDeleteTarget(null);
    setOptionsRetryKey((value) => value + 1);
    if (records.length === 1 && page > 1) {
      setPage((current) => Math.max(1, current - 1));
    } else {
      setRetryKey((value) => value + 1);
    }
  }

  function refreshAfterBulk() {
    setImportOpen(false);
    setDeleteAllOpen(false);
    setOptionsRetryKey((value) => value + 1);
    if (page !== 1) {
      setPage(1);
    } else {
      setRetryKey((value) => value + 1);
    }
  }

  async function exportRecords() {
    if (exportInFlight.current) return;
    exportInFlight.current = true;
    setExporting(true);
    try {
      downloadFirmwareXlsx(await exportFirmware(filters));
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      exportInFlight.current = false;
      setExporting(false);
    }
  }

  const filtersActive = Object.values(filters).some(Boolean);
  const totalPages = pagination.total_pages || 0;

  return (
    <OrdinaryPageBody data-testid="firmware-page">
      <OrdinaryPageHeader
        title="Firmware"
        actions={
          <>
          <Button
            type="button"
            variant="export"
            onClick={exportRecords}
            loading={exporting}
            loadingLabel="Exporting…"
            data-action-category="export-download"
            data-testid="firmware-export"
          >
            <Download aria-hidden="true" />
            Export
          </Button>
          {isAdmin ? (
            <>
              <Button
                type="button"
                variant="import"
                onClick={() => setImportOpen(true)}
                data-action-category="import-upload"
                data-testid="firmware-import"
              >
                <Upload aria-hidden="true" /> Import
              </Button>
              <Button
                type="button"
                variant="transfer-remove"
                onClick={() => setDeleteAllOpen(true)}
                data-action-category="destructive"
                data-testid="firmware-delete-all"
              >
                <Trash2 aria-hidden="true" /> Delete All
              </Button>
              <Button onClick={() => setEditing(null)} data-testid="firmware-add">
                <Plus aria-hidden="true" /> Add Firmware
              </Button>
            </>
          ) : null}
          </>
        }
        data-testid="firmware-page-header"
      />

      <ListTableShell data-testid="firmware-data-panel">
      {optionsError ? <p className="border-b border-zinc-200 px-5 py-3 text-xs text-warning-foreground dark:border-zinc-800" role="status">{optionsError}</p> : null}
        <ListTableViewport>
          <ListTable geometry={isAdmin ? FIRMWARE_TABLE_GEOMETRY : FIRMWARE_STAFF_TABLE_GEOMETRY} data-testid="firmware-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Firmware Version</th>
                <th>Type</th>
                <th>Android Version</th>
                <th>Firmware</th>
                <th>APDB</th>
                <th>Status</th>
                <th>Updated At</th>
                {isAdmin ? <th>Actions</th> : null}
              </tr>
              <ColumnFilterRow data-testid="firmware-column-filter-row">
                <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.model} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, model: value }))} onApply={(value) => updateFilter("model", value)} options={filterOptions.models} placeholder="All" aria-label="Filter Model" data-testid="firmware-model-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnTextFilter value={filterDrafts.firmware_version} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, firmware_version: value }))} onApply={(value) => updateFilter("firmware_version", value)} placeholder="Filter…" aria-label="Filter Firmware Version" data-testid="firmware-version-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.firmware_type} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, firmware_type: value }))} onApply={(value) => updateFilter("firmware_type", value)} options={filterOptions.firmware_types} placeholder="All" aria-label="Filter Type" data-testid="firmware-type-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.android_version} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, android_version: value }))} onApply={(value) => updateFilter("android_version", value)} options={filterOptions.android_versions} placeholder="All" aria-label="Filter Android Version" data-testid="firmware-android-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnTextFilter value={filterDrafts.firmware_url} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, firmware_url: value }))} onApply={(value) => updateFilter("firmware_url", value)} placeholder="Filter…" aria-label="Filter Firmware" data-testid="firmware-url-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnTextFilter value={filterDrafts.apdb_url} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, apdb_url: value }))} onApply={(value) => updateFilter("apdb_url", value)} placeholder="Filter…" aria-label="Filter APDB" data-testid="firmware-apdb-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.status} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, status: value }))} onApply={(value) => updateFilter("status", value)} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} placeholder="All" aria-label="Filter Status" data-testid="firmware-status-filter" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnDateFilter value={filterDrafts.updated_date} onValueChange={(value) => setFilterDrafts((current) => ({ ...current, updated_date: value }))} onApply={(value) => updateFilter("updated_date", value)} aria-label="Filter Updated At" data-testid="firmware-updated-date-filter" /></ColumnFilterCell>
                {isAdmin ? <ColumnFilterCell className="align-middle" aria-label="Actions filter controls"><div className="flex h-7 w-full items-center justify-end">{filtersActive ? <ClearAllFilters onClick={clearFilters} data-testid="firmware-filter-clear-all" /> : null}</div></ColumnFilterCell> : null}
              </ColumnFilterRow>
            </thead>
            <tbody>
              {loading ? (
                <ListTableState colSpan={isAdmin ? 9 : 8} kind="loading">Loading firmware records…</ListTableState>
              ) : error ? (
                <ListTableState colSpan={isAdmin ? 9 : 8} kind="error" onRetry={() => setRetryKey((value) => value + 1)}>{error}</ListTableState>
              ) : records.length === 0 ? (
                <ListTableState colSpan={isAdmin ? 9 : 8}>
                  <div>
                    <p className="text-sm font-medium">
                  {filtersActive ? "No firmware records match these filters." : "No firmware records found."}
                    </p>
                  </div>
                </ListTableState>
              ) : records.map((record) => (
                <tr key={record.id} data-testid="firmware-row">
                  <td className="font-semibold"><DataValue value={record.model} /></td>
                  <td className="max-w-[180px] tabular-nums"><DataValue value={record.firmware_version} /></td>
                  <td className="max-w-[180px]"><DataValue value={record.firmware_type} /></td>
                  <td className="max-w-[150px]"><DataValue value={record.android_version} /></td>
                  <td>
                    <ExternalFirmwareLink href={record.firmware_url} label={`${record.model} firmware`} testid={`firmware-link-${record.id}`} />
                  </td>
                  <td>
                    <ExternalFirmwareLink href={record.apdb_url} label={`${record.model} APDB`} testid={`apdb-link-${record.id}`} />
                  </td>
                  <td>
                    <PassiveBadge
                      tone={record.status === "active" ? "success" : "neutral"}
                      data-testid={`firmware-status-${record.id}`}
                    >
                      {record.status === "active" ? "Active" : "Inactive"}
                    </PassiveBadge>
                  </td>
                  <td className="whitespace-nowrap text-xs tabular-nums">{formatUpdatedAt(record.updated_at)}</td>
                  {isAdmin ? (
                    <td>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="edit"
                          size="row-icon"
                          onClick={() => setEditing(record)}
                          title="Edit firmware"
                          aria-label={`Edit firmware ${record.model} ${record.firmware_version}`}
                          data-testid={`firmware-edit-${record.id}`}
                        >
                          <Edit3 aria-hidden="true" />
                        </Button>
                        <Button
                          type="button"
                          variant="transfer-remove"
                          size="row-icon"
                          onClick={() => setDeleteTarget(record)}
                          title="Delete firmware"
                          aria-label={`Delete firmware ${record.model} ${record.firmware_version}`}
                          data-testid={`firmware-delete-${record.id}`}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </ListTable>
        </ListTableViewport>
        <ListPagination
          page={pagination.page}
          pageSize={pageSize}
          total={pagination.total}
          totalPages={totalPages}
          loading={loading}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
          pageSizeAriaLabel="Firmware rows per page"
          summaryTestId="firmware-page-summary"
          pageTestId="firmware-page-number"
          previousTestId="firmware-previous-page"
          nextTestId="firmware-next-page"
        />
      </ListTableShell>
      <FirmwareFormDialog
        open={editing !== undefined}
        record={editing || null}
        onClose={() => setEditing(undefined)}
        onSaved={() => refreshAfterSave(editing === null)}
      />
      <FirmwareDeleteDialog
        open={Boolean(deleteTarget)}
        record={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={refreshAfterDelete}
      />
      <FirmwareImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refreshAfterBulk}
      />
      <FirmwareDeleteAllDialog
        open={deleteAllOpen}
        onClose={() => setDeleteAllOpen(false)}
        onDeleted={refreshAfterBulk}
      />
    </OrdinaryPageBody>
  );
}
